'use client'

/**
 * VisualBlocks — inline data visualizations for assistant messages.
 *
 * The assistant emits fenced code blocks with custom language tags
 * (```42a:metric, ```42a:bar, ```42a:trend) containing a JSON spec.
 * MessageList's ReactMarkdown code renderer routes those here.
 *
 * Behavior:
 * - Valid spec   → render the matching design-system visualization.
 * - Invalid JSON while the message is still streaming → skeleton (the fence
 *   simply hasn't closed yet).
 * - Invalid JSON on a complete message → plain code block fallback, never a crash.
 */

import { memo } from 'react'
import { Skeleton, TrendChart } from '@/design-system'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import WebsiteFavicon from '@/components/WebsiteFavicon'

export const VISUAL_BLOCK_LANGUAGES = ['42a:metric', '42a:bar', '42a:trend', '42a:domains', '42a:prompts', '42a:comparison'] as const
export type VisualBlockLanguage = (typeof VISUAL_BLOCK_LANGUAGES)[number]

export function isVisualBlockLanguage(lang: string): lang is VisualBlockLanguage {
  return (VISUAL_BLOCK_LANGUAGES as readonly string[]).includes(lang)
}

// ---------------------------------------------------------------------------
// Specs
// ---------------------------------------------------------------------------

interface MetricSpec {
  label: string
  value: string | number
  delta?: string
  trend?: 'up' | 'down' | 'flat'
}

interface BarItem {
  label: string
  value: number
}

interface BarSpec {
  title?: string
  items: BarItem[]
  unit?: string
}

interface TrendSpec {
  title?: string
  points: number[]
  labels?: string[]
}

interface DomainItem {
  domain: string
  citations?: number
  providers?: string[]
}

interface DomainsSpec {
  title?: string
  items: DomainItem[]
}

interface PromptsYou {
  name: string
  mentions: number
  rank?: number
}

interface PromptsAheadItem {
  name: string
  mentions: number
}

interface PromptsItem {
  prompt: string
  ahead: PromptsAheadItem[]
  you?: PromptsYou
}

interface PromptsSpec {
  title?: string
  items: PromptsItem[]
}

interface ComparisonRow {
  label: string
  you?: boolean
  domain?: string
  values: string[]
}

interface ComparisonSpec {
  title?: string
  columns: string[]
  rows: ComparisonRow[]
}

function parseSpec(raw: string): unknown | null {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Block renderers
// ---------------------------------------------------------------------------

function MetricBlock({ spec }: { spec: MetricSpec }) {
  const TrendIcon =
    spec.trend === 'up' ? TrendingUp : spec.trend === 'down' ? TrendingDown : Minus
  const trendColor =
    spec.trend === 'up'
      ? 'var(--color-success, #059669)'
      : spec.trend === 'down'
        ? 'var(--color-pink)'
        : 'var(--color-text-muted)'

  return (
    <div className="assistant-visual-block">
      <div
        style={{
          fontSize: '0.68rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--color-text-muted)',
        }}
      >
        {spec.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <span
          style={{
            fontSize: '1.6rem',
            fontWeight: 700,
            lineHeight: 1.2,
            background: 'var(--gradient-lime-cyan)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {spec.value}
        </span>
        {spec.delta && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.2rem',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: trendColor,
            }}
          >
            <TrendIcon style={{ width: '13px', height: '13px' }} />
            {spec.delta}
          </span>
        )}
      </div>
    </div>
  )
}

function BarBlock({ spec }: { spec: BarSpec }) {
  const max = Math.max(...spec.items.map((i) => i.value), 0)

  return (
    <div className="assistant-visual-block">
      {spec.title && <BlockTitle title={spec.title} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {spec.items.map((item) => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              title={item.label}
              style={{
                width: '92px',
                flexShrink: 0,
                fontSize: '0.72rem',
                color: 'var(--color-text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </span>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                height: '14px',
                borderRadius: '4px',
                background: 'var(--color-surface-hover)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: max > 0 ? `${Math.max((item.value / max) * 100, 2)}%` : '2%',
                  height: '100%',
                  borderRadius: '4px',
                  background: 'var(--gradient-lime-cyan)',
                }}
              />
            </div>
            <span
              style={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                textAlign: 'right',
                fontSize: '0.72rem',
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--color-text-primary)',
              }}
            >
              {item.value.toLocaleString()}
              {spec.unit ? (
                <span style={{ marginLeft: '0.25rem', fontWeight: 400, color: 'var(--color-text-muted)' }}>
                  {spec.unit}
                </span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function TrendBlock({ spec }: { spec: TrendSpec }) {
  const data = spec.points.map((value, i) => ({
    date: spec.labels?.[i] ?? String(i + 1),
    value,
  }))

  return (
    <div className="assistant-visual-block">
      {spec.title && <BlockTitle title={spec.title} />}
      <TrendChart
        data={data}
        series={[{ dataKey: 'value', color: 'lime' }]}
        height={120}
        showGrid={false}
        showYAxis={false}
      />
    </div>
  )
}

/** Short label + accent for known LLM providers; anything else renders muted. */
const PROVIDER_CHIP: Record<string, { label: string; color: string; bg: string }> = {
  openai: { label: 'GPT', color: 'var(--color-lime)', bg: 'var(--color-lime-muted)' },
  perplexity: { label: 'PPX', color: 'var(--color-cyan)', bg: 'var(--color-cyan-muted)' },
  google: { label: 'GEM', color: 'var(--color-purple)', bg: 'var(--color-purple-muted)' },
  anthropic: { label: 'CLD', color: 'var(--color-pink)', bg: 'var(--color-pink-muted)' },
  ai_overview: { label: 'AIO', color: 'var(--color-purple)', bg: 'var(--color-purple-muted)' },
}

const MAX_PROVIDER_CHIPS = 3

function ProviderChips({ providers }: { providers: string[] }) {
  const shown = providers.slice(0, MAX_PROVIDER_CHIPS)
  const overflow = providers.length - shown.length

  return (
    <span style={{ display: 'flex', gap: '0.2rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      {shown.map((p) => {
        const known = PROVIDER_CHIP[p.toLowerCase()]
        return (
          <span
            key={p}
            title={p}
            style={{
              fontSize: '0.55rem',
              fontWeight: 600,
              borderRadius: '9999px',
              padding: '0.08rem 0.38rem',
              color: known?.color ?? 'var(--color-text-muted)',
              background: known?.bg ?? 'var(--color-surface-hover)',
              textTransform: 'uppercase',
            }}
          >
            {known?.label ?? p.slice(0, 3)}
          </span>
        )
      })}
      {overflow > 0 && (
        <span
          title={providers.slice(MAX_PROVIDER_CHIPS).join(', ')}
          style={{
            fontSize: '0.55rem',
            fontWeight: 600,
            borderRadius: '9999px',
            padding: '0.08rem 0.38rem',
            color: 'var(--color-text-muted)',
            background: 'var(--color-surface-hover)',
          }}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}

function DomainsBlock({ spec }: { spec: DomainsSpec }) {
  const t = useTranslations('assistant.visualBlocks')
  const hasCitations = spec.items.some((i) => typeof i.citations === 'number')
  const hasProviders = spec.items.some((i) => i.providers && i.providers.length > 0)
  const max = Math.max(...spec.items.map((i) => i.citations ?? 0), 0)

  const gridTemplate = `1fr${hasCitations ? ' 64px' : ''}${hasProviders ? ' 92px' : ''}`

  return (
    <div className="assistant-visual-block">
      {spec.title && <BlockTitle title={spec.title} />}
      <div
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: '0.75rem',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            gap: '0.5rem',
            alignItems: 'center',
            padding: '0.4rem 0.75rem',
            background: 'var(--color-surface-hover)',
            fontSize: '0.6rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--color-text-muted)',
          }}
        >
          <span>{t('columnDomain')}</span>
          {hasCitations && <span style={{ textAlign: 'right' }}>{t('columnCitations')}</span>}
          {hasProviders && <span style={{ textAlign: 'right' }}>{t('columnProviders')}</span>}
        </div>
        {spec.items.map((item) => (
          <div
            key={item.domain}
            style={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              borderTop: '1px solid var(--color-border)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
              <WebsiteFavicon url={item.domain} fallbackText={item.domain} size="small" />
              <span
                title={item.domain}
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.domain}
              </span>
            </span>
            {hasCitations && (
              <span style={{ textAlign: 'right' }}>
                {typeof item.citations === 'number' ? (
                  <>
                    <span
                      style={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {item.citations}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        height: '3px',
                        borderRadius: '2px',
                        background: 'var(--gradient-lime-cyan)',
                        marginTop: '3px',
                        marginLeft: 'auto',
                        width: max > 0 ? `${Math.max((item.citations / max) * 100, 4)}%` : '4%',
                      }}
                    />
                  </>
                ) : (
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>—</span>
                )}
              </span>
            )}
            {hasProviders && <ProviderChips providers={item.providers ?? []} />}
          </div>
        ))}
      </div>
    </div>
  )
}

function PromptsBlock({ spec }: { spec: PromptsSpec }) {
  return (
    <div className="assistant-visual-block">
      {spec.title && <BlockTitle title={spec.title} />}
      <div
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: '0.75rem',
          overflow: 'hidden',
        }}
      >
        {spec.items.map((item, idx) => (
          <div
            key={idx}
            style={{
              padding: '0.6rem 0.75rem',
              borderTop: idx === 0 ? 'none' : '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: '0.78rem',
                fontWeight: 500,
                color: 'var(--color-text-primary)',
                marginBottom: '0.4rem',
                lineHeight: 1.4,
              }}
            >
              {item.prompt}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
              {item.ahead.map((brand) => (
                <span
                  key={brand.name}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    borderRadius: '9999px',
                    padding: '0.12rem 0.5rem',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    background: 'var(--color-pink-muted)',
                    color: 'var(--color-pink)',
                  }}
                >
                  {brand.name}
                  <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
                    {brand.mentions}
                  </span>
                </span>
              ))}
              {item.you && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    borderRadius: '9999px',
                    padding: '0.12rem 0.5rem',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    background: 'var(--color-lime-muted)',
                    color: 'var(--color-lime)',
                    border: '1px dashed var(--color-lime)',
                  }}
                >
                  {item.you.name}
                  <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
                    {item.you.mentions}
                  </span>
                  {item.you.rank !== undefined && (
                    <span
                      style={{
                        fontSize: '0.6rem',
                        color: 'var(--color-text-muted)',
                        marginLeft: '0.15rem',
                      }}
                    >
                      #{item.you.rank}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ComparisonBlock({ spec }: { spec: ComparisonSpec }) {
  const t = useTranslations('assistant.visualBlocks')
  const colCount = spec.columns.length
  const gridTemplateColumns = `1.4fr ${Array(colCount).fill('1fr').join(' ')}`

  return (
    <div className="assistant-visual-block">
      {spec.title && <BlockTitle title={spec.title} />}
      <div
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: '0.75rem',
          overflow: 'hidden',
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gap: '0.4rem',
            alignItems: 'center',
            padding: '0.4rem 0.75rem',
            background: 'var(--color-surface-hover)',
            fontSize: '0.58rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--color-text-muted)',
          }}
        >
          <span>{t('columnBrand')}</span>
          {spec.columns.map((col) => (
            <span key={col} style={{ textAlign: 'right' }}>
              {col}
            </span>
          ))}
        </div>

        {/* Data rows */}
        {spec.rows.map((row, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns,
              gap: '0.4rem',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              borderTop: '1px solid var(--color-border)',
              background: row.you ? 'var(--color-lime-muted)' : undefined,
              fontSize: '0.78rem',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: 600 }}>
              {row.domain && (
                <WebsiteFavicon url={row.domain} fallbackText={row.label} size="small" />
              )}
              {row.label}
              {row.you && (
                <span
                  style={{
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    color: 'var(--color-lime)',
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-lime)',
                    borderRadius: '9999px',
                    padding: '0.02rem 0.3rem',
                  }}
                >
                  {t('youBadge')}
                </span>
              )}
            </span>
            {row.values.map((val, vi) => (
              <span
                key={vi}
                style={{
                  textAlign: 'right',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {val}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function BlockTitle({ title }: { title: string }) {
  return (
    <div
      style={{
        fontSize: '0.68rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--color-text-muted)',
        marginBottom: '0.35rem',
      }}
    >
      {title}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Spec validation (minimal shape checks — never trust model output blindly)
// ---------------------------------------------------------------------------

function isMetricSpec(s: unknown): s is MetricSpec {
  if (typeof s !== 'object' || s === null) return false
  const o = s as Record<string, unknown>
  return (
    typeof o.label === 'string' &&
    (typeof o.value === 'string' || typeof o.value === 'number')
  )
}

function isBarSpec(s: unknown): s is BarSpec {
  if (typeof s !== 'object' || s === null) return false
  const o = s as Record<string, unknown>
  return (
    Array.isArray(o.items) &&
    o.items.length > 0 &&
    o.items.every(
      (i) =>
        typeof i === 'object' &&
        i !== null &&
        typeof (i as Record<string, unknown>).label === 'string' &&
        typeof (i as Record<string, unknown>).value === 'number',
    )
  )
}

function isTrendSpec(s: unknown): s is TrendSpec {
  if (typeof s !== 'object' || s === null) return false
  const o = s as Record<string, unknown>
  return (
    Array.isArray(o.points) &&
    o.points.length > 1 &&
    o.points.every((p) => typeof p === 'number')
  )
}

function isDomainsSpec(s: unknown): s is DomainsSpec {
  if (typeof s !== 'object' || s === null) return false
  const o = s as Record<string, unknown>
  return (
    Array.isArray(o.items) &&
    o.items.length > 0 &&
    o.items.every((i) => {
      if (typeof i !== 'object' || i === null) return false
      const item = i as Record<string, unknown>
      return (
        typeof item.domain === 'string' &&
        (item.citations === undefined || typeof item.citations === 'number') &&
        (item.providers === undefined ||
          (Array.isArray(item.providers) && item.providers.every((p) => typeof p === 'string')))
      )
    })
  )
}

function isPromptsSpec(s: unknown): s is PromptsSpec {
  if (typeof s !== 'object' || s === null) return false
  const o = s as Record<string, unknown>
  return (
    Array.isArray(o.items) &&
    o.items.length > 0 &&
    o.items.every((i) => {
      if (typeof i !== 'object' || i === null) return false
      const item = i as Record<string, unknown>
      if (typeof item.prompt !== 'string') return false
      if (
        !Array.isArray(item.ahead) ||
        !item.ahead.every(
          (a) =>
            typeof a === 'object' &&
            a !== null &&
            typeof (a as Record<string, unknown>).name === 'string' &&
            typeof (a as Record<string, unknown>).mentions === 'number',
        )
      )
        return false
      if (item.you !== undefined) {
        if (typeof item.you !== 'object' || item.you === null) return false
        const y = item.you as Record<string, unknown>
        if (typeof y.name !== 'string' || typeof y.mentions !== 'number') return false
      }
      return true
    })
  )
}

function isComparisonSpec(s: unknown): s is ComparisonSpec {
  if (typeof s !== 'object' || s === null) return false
  const o = s as Record<string, unknown>
  if (!Array.isArray(o.columns) || !o.columns.every((c) => typeof c === 'string')) return false
  const colLen = (o.columns as string[]).length
  return (
    Array.isArray(o.rows) &&
    o.rows.length > 0 &&
    o.rows.every((r) => {
      if (typeof r !== 'object' || r === null) return false
      const row = r as Record<string, unknown>
      return (
        typeof row.label === 'string' &&
        Array.isArray(row.values) &&
        row.values.length === colLen &&
        row.values.every((v) => typeof v === 'string')
      )
    })
  )
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

interface VisualBlockProps {
  language: VisualBlockLanguage
  raw: string
  /** True while the parent message is still streaming — bad JSON shows a skeleton. */
  isStreaming: boolean
}

/**
 * Memoized: all props are primitives, so the default shallow compare skips
 * re-renders (and re-parses, and recharts work) whenever a parent re-renders
 * without the underlying fence content changing.
 */
export const VisualBlock = memo(function VisualBlock({
  language,
  raw,
  isStreaming,
}: VisualBlockProps) {
  const spec = parseSpec(raw)

  const valid =
    spec !== null &&
    ((language === '42a:metric' && isMetricSpec(spec)) ||
      (language === '42a:bar' && isBarSpec(spec)) ||
      (language === '42a:trend' && isTrendSpec(spec)) ||
      (language === '42a:domains' && isDomainsSpec(spec)) ||
      (language === '42a:prompts' && isPromptsSpec(spec)) ||
      (language === '42a:comparison' && isComparisonSpec(spec)))

  if (!valid) {
    if (isStreaming) {
      // The fence likely hasn't closed yet — show a placeholder.
      return (
        <div className="assistant-visual-block">
          <Skeleton className="h-16 w-full" />
        </div>
      )
    }
    // Complete but malformed: degrade to a plain code block, never crash.
    return (
      <pre>
        <code>{raw}</code>
      </pre>
    )
  }

  if (language === '42a:metric') return <MetricBlock spec={spec as MetricSpec} />
  if (language === '42a:bar') return <BarBlock spec={spec as BarSpec} />
  if (language === '42a:domains') return <DomainsBlock spec={spec as DomainsSpec} />
  if (language === '42a:prompts') return <PromptsBlock spec={spec as PromptsSpec} />
  if (language === '42a:comparison') return <ComparisonBlock spec={spec as ComparisonSpec} />
  return <TrendBlock spec={spec as TrendSpec} />
})
